import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faChevronLeft,
  faChevronRight,
  faHeart,
  faShare,
  faShoppingCart,
  faExchangeAlt,
  faShippingFast,
  faShieldAlt,
  faStar,
  faCheck,
  faRuler,
  faWeight,
  faBox,
} from "@fortawesome/free-solid-svg-icons";
import styles from "./QuickViewModal.module.css";

const QuickViewModal = ({
  product,
  series,
  isOpen,
  onClose,
  onAddToCart,
}: any) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedColor, setSelectedColor] = useState("");
  const [selectedVariant, setSelectedVariant] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [isWishlisted, setIsWishlisted] = useState(false);

  // Reset states when product changes
  useEffect(() => {
    if (product) {
      setCurrentImageIndex(0);
      setQuantity(1);
      // Set default color and variant if available
      if (product.color) setSelectedColor(product.color);
    }
  }, [product]);

  if (!product || !isOpen) return null;

  const allImages = product.picture?.map((img: any) => img.path) || [];
  const discount =
    product.price > 0
      ? Math.round(((product.price - product.ourPrice) / product.price) * 100)
      : 0;

  const handleImageNavigation = (direction: any) => {
    if (direction === "prev") {
      setCurrentImageIndex((prev) =>
        prev === 0 ? allImages.length - 1 : prev - 1
      );
    } else {
      setCurrentImageIndex((prev) =>
        prev === allImages.length - 1 ? 0 : prev + 1
      );
    }
  };

  const handleThumbnailClick = (index: any) => {
    setCurrentImageIndex(index);
  };

  const handleQuantityChange = (change: any) => {
    setQuantity((prev) => Math.max(1, prev + change));
  };

  const handleAddToCart = () => {
    if (onAddToCart) {
      onAddToCart({
        ...product,
        quantity,
        selectedColor,
        selectedVariant,
      });
    }
  };

  const handleKeyDown = (e: any) => {
    if (e.key === "Escape") onClose();
    if (e.key === "ArrowLeft") handleImageNavigation("prev");
    if (e.key === "ArrowRight") handleImageNavigation("next");
  };

  // Mock data for demonstration
  const availableColors = [
    "Olive Green",
    "Walnut Brown",
    "Ebony Black",
    "Maple",
  ];
  const availableVariants = ["Standard", "Premium", "Executive"];
  const features = [
    { icon: faShippingFast, text: "Free Shipping" },
    { icon: faShieldAlt, text: "1 Year Warranty" },
    { icon: faBox, text: "Knockdown Delivery" },
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className={styles.modalBackdrop}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          {/* Modal */}
          <div
            className={styles.modalContainer}
            onKeyDown={handleKeyDown}
            tabIndex={0}
          >
            <motion.div
              className={styles.modalContent}
              initial={{ opacity: 0, scale: 0.9, y: 50 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 50 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
            >
              {/* Close Button */}
              <button className={styles.closeButton} onClick={onClose}>
                <FontAwesomeIcon icon={faTimes} />
              </button>

              <div className={styles.modalBody}>
                {/* Left Column - Images */}
                <div className={styles.imageSection}>
                  {/* Main Image */}
                  <div className={styles.mainImageContainer}>
                    <motion.img
                      key={currentImageIndex}
                      src={allImages[currentImageIndex]}
                      alt={product.name}
                      className={styles.mainImage}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.3 }}
                    />

                    {/* Navigation Arrows */}
                    {allImages.length > 1 && (
                      <>
                        <button
                          className={`${styles.navArrow} ${styles.prevArrow}`}
                          onClick={() => handleImageNavigation("prev")}
                        >
                          <FontAwesomeIcon icon={faChevronLeft} />
                        </button>
                        <button
                          className={`${styles.navArrow} ${styles.nextArrow}`}
                          onClick={() => handleImageNavigation("next")}
                        >
                          <FontAwesomeIcon icon={faChevronRight} />
                        </button>
                      </>
                    )}

                    {/* Discount Badge */}
                    {discount > 0 && (
                      <div className={styles.discountBadge}>
                        {discount}% OFF
                      </div>
                    )}
                  </div>

                  {/* Image Thumbnails */}
                  {allImages.length > 1 && (
                    <div className={styles.thumbnailContainer}>
                      {allImages.map((image: any, index: any) => (
                        <button
                          key={index}
                          className={`${styles.thumbnail} ${
                            index === currentImageIndex
                              ? styles.activeThumbnail
                              : ""
                          }`}
                          onClick={() => handleThumbnailClick(index)}
                        >
                          <img src={image} alt={`Thumbnail ${index + 1}`} />
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Right Column - Product Details */}
                <div className={styles.detailsSection}>
                  {/* Header */}
                  <div className={styles.productHeader}>
                    <div className={styles.categoryBadge}>
                      {series?.title || "Office Furniture"}
                    </div>
                    <h2 className={styles.productTitle}>{product.name}</h2>
                    <div className={styles.productCode}>
                      Product Code: {product.productId}
                    </div>
                  </div>

                  {/* Rating */}
                  <div className={styles.ratingSection}>
                    <div className={styles.stars}>
                      {[...Array(5)].map((_, i) => (
                        <FontAwesomeIcon
                          key={i}
                          icon={faStar}
                          className={
                            i < 4 ? styles.filledStar : styles.emptyStar
                          }
                        />
                      ))}
                    </div>
                    <span className={styles.ratingText}>
                      (4.5) • 12 Reviews
                    </span>
                  </div>

                  {/* Pricing */}
                  <div className={styles.pricingSection}>
                    {discount > 0 ? (
                      <>
                        <div className={styles.originalPrice}>
                          ₹{product.price?.toLocaleString()}
                        </div>
                        <div className={styles.discountPrice}>
                          ₹{product.ourPrice?.toLocaleString()}
                          <span className={styles.priceUnit}> / piece</span>
                        </div>
                        <div className={styles.saveAmount}>
                          You save ₹
                          {(product.price - product.ourPrice)?.toLocaleString()}
                        </div>
                      </>
                    ) : (
                      <div className={styles.regularPrice}>
                        ₹{product.ourPrice?.toLocaleString()}
                        <span className={styles.priceUnit}> / piece</span>
                      </div>
                    )}
                  </div>

                  {/* Color Selection */}
                  <div className={styles.optionSection}>
                    <label className={styles.optionLabel}>Color</label>
                    <div className={styles.colorOptions}>
                      {availableColors.map((color) => (
                        <button
                          key={color}
                          className={`${styles.colorOption} ${
                            selectedColor === color ? styles.selectedColor : ""
                          }`}
                          onClick={() => setSelectedColor(color)}
                        >
                          <div
                            className={styles.colorSwatch}
                            style={{
                              backgroundColor: color
                                .toLowerCase()
                                .includes("green")
                                ? "#8FBC8F"
                                : color.toLowerCase().includes("brown")
                                ? "#8B4513"
                                : color.toLowerCase().includes("black")
                                ? "#2C3E50"
                                : "#D2B48C",
                            }}
                          />
                          <span>{color}</span>
                          {selectedColor === color && (
                            <FontAwesomeIcon
                              icon={faCheck}
                              className={styles.checkIcon}
                            />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Variant Selection */}
                  <div className={styles.optionSection}>
                    <label className={styles.optionLabel}>Variant</label>
                    <div className={styles.variantOptions}>
                      {availableVariants.map((variant) => (
                        <button
                          key={variant}
                          className={`${styles.variantOption} ${
                            selectedVariant === variant
                              ? styles.selectedVariant
                              : ""
                          }`}
                          onClick={() => setSelectedVariant(variant)}
                        >
                          {variant}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Quantity */}
                  <div className={styles.optionSection}>
                    <label className={styles.optionLabel}>Quantity</label>
                    <div className={styles.quantitySelector}>
                      <button
                        className={styles.quantityBtn}
                        onClick={() => handleQuantityChange(-1)}
                        disabled={quantity <= 1}
                      >
                        -
                      </button>
                      <span className={styles.quantityDisplay}>{quantity}</span>
                      <button
                        className={styles.quantityBtn}
                        onClick={() => handleQuantityChange(1)}
                      >
                        +
                      </button>
                    </div>
                  </div>

                  {/* Key Features */}
                  <div className={styles.featuresSection}>
                    <h4 className={styles.featuresTitle}>Key Features</h4>
                    <div className={styles.featuresGrid}>
                      {features.map((feature, index) => (
                        <div key={index} className={styles.featureItem}>
                          <FontAwesomeIcon icon={feature.icon} />
                          <span>{feature.text}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Product Attributes */}
                  {product.attributes && (
                    <div className={styles.attributesSection}>
                      <h4 className={styles.attributesTitle}>Specifications</h4>
                      <div className={styles.attributesGrid}>
                        {Object.entries(product?.attributes).map(
                          ([key, value]: any) => (
                            <div key={key} className={styles.attributeRow}>
                              <span className={styles.attributeKey}>
                                {key.trim()}:
                              </span>
                              <span className={styles.attributeValue}>
                                {value}
                              </span>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className={styles.actionSection}>
                    <div className={styles.primaryActions}>
                      <motion.button
                        className={styles.addToCartBtn}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={handleAddToCart}
                      >
                        <FontAwesomeIcon icon={faShoppingCart} />
                        Add to Cart
                      </motion.button>

                      <motion.button
                        className={styles.buyNowBtn}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        Buy Now
                      </motion.button>
                    </div>

                    <div className={styles.secondaryActions}>
                      <button
                        className={styles.wishlistBtn}
                        onClick={() => setIsWishlisted(!isWishlisted)}
                      >
                        <FontAwesomeIcon
                          icon={faHeart}
                          className={isWishlisted ? styles.wishlisted : ""}
                        />
                        {isWishlisted ? "Wishlisted" : "Add to Wishlist"}
                      </button>

                      <button className={styles.shareBtn}>
                        <FontAwesomeIcon icon={faShare} />
                        Share
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default QuickViewModal;
